import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  faCloudArrowDown,
  faFileImport,
  faFileInvoice,
  faGears,
  faNewspaper,
  faShield,
  faTableColumns,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { environment } from "../../../environments/environment";
import { AuthService } from "../../services/auth.service";
import { Roles } from "../auth/auth.role";
import { AuthToken } from "../auth/auth.token";

@Component({
  selector: "app-nav-bar",
  templateUrl: "./nav-bar.component.html",
  styleUrls: ["./nav-bar.component.css"],
})
export class NavBarComponent implements OnInit {
  faTableColumns = faTableColumns;
  faFileInvoice = faFileInvoice;
  faFileImport = faFileImport;
  faCloudArrowDown = faCloudArrowDown;
  faShield = faShield;
  faNewspaper = faNewspaper;
  faUser = faUser;
  faGears = faGears;
  logoPath = `assets/img/${environment.name}/logo.png`;
  isNotProduction = environment.name != "production";

  public auth: AuthToken = null;
  public roles = Roles;

  signout(): void {
    this.authService.logout();
    this.router.navigate(["/login"]);
  }

  changePassword(): void {
    this.authService.logout();
    this.router.navigate(["/password-reset", this.auth.username]);
  }

  constructor(private router: Router, private authService: AuthService) {
    this.auth = authService.getAuth();
  }

  ngOnInit() {}

  canShow(roles: Roles[]): boolean {
    return this.authService.hasRoles(roles);
  }

  /**
   * @description Check if can create any ticket
   * @returns true if user has the role to create ticket
   */
  canCreateAny(): boolean {
    const roles = [
      Roles.CreateTicketFundQuota,
      Roles.CreateTicketPrivateFixedIncome,
      Roles.CreateTicketPublicFixedIncome,
      Roles.CreateTicketFutures,
      Roles.CreateTicketMargin,
      Roles.CreateTicketContracted,
      Roles.CreateTicketCurrencyTerm,
      Roles.CreateTicketVariableIncome,
      Roles.CreateTicketSwapCetip,
      Roles.CreateTicketExchange,
      Roles.CreateLiabilitiesTicket,
      Roles.CreateTicketStructuredOperations,
      Roles.CreateStructuredOperationOrderTicket,
    ];

    return this.authService.hasRoles(roles);
  }

  canUploadAny(): boolean {
    const roles = [Roles.CreateTicketVariableIncome];

    return this.authService.hasRoles(roles);
  }

  /***
   * @description Check if can view any ticket
   * @returns true if user has roles
   */
  canViewMonitor(): boolean {
    const roles = [
      Roles.ViewMonitorFundQuota,
      Roles.ViewMonitorPrivateFixedIncome,
      Roles.ViewMonitorPublicFixedIncome,
      Roles.ViewMonitorFutures,
      Roles.ViewMonitorMargin,
      Roles.ViewMonitorContracted,
      Roles.ViewMonitorCurrencyTerm,
      Roles.ViewMonitorVariableIncome,
      Roles.ViewMonitorSwapCetip,
      Roles.ViewMonitorExchange,
      Roles.ViewReceivable,
      Roles.ViewLiabilitiesMonitor,
      Roles.ViewMonitorOperationStructured,
      Roles.ViewStructuredOperationOrderMonitor,
    ];

    return this.authService.hasRoles(roles);
  }

  /**
   * @description Check if can change cutoff default or extend time limit
   * @returns true if user has roles
   */
  canChangeCutoff(): boolean {
    const roles = [Roles.ChangeDefaultLimitTime, Roles.ChangeLimitTime];
    return this.authService.hasRoles(roles);
  }

  /**
   * @description Check if can show security menu options
   * @returns true if user has some of the roles
   */
  canShowSecurity(): boolean {
    const roles = [
      Roles.CreateMasterUser,
      Roles.CreateSubordinateUser,
      Roles.ChangeDefaultLimitTime,
      Roles.ChangeLimitTime,
    ];

    return this.authService.hasRoles(roles);
  }

  canShowUtility(): boolean {
    const roles = [Roles.RegisterBank];
    return this.authService.hasRoles(roles);
  }

  canShowUploadTransfer(): boolean {
    const roles = [
      Roles.UploadTransfer,
      Roles.ApproveUploadTransfer,
      Roles.ViewUploadTransferMonitor,
    ];

    return this.authService.hasRoles(roles);
  }

  /**
   * @description Toggle submenu visibility on click
   * @param event Click event
   */
  toggleSubmenu(event: Event): void {
    // Prevent event propagation to avoid closing the main dropdown
    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement;
    const submenu = target.closest(".dropdown-submenu");
    const dropdownMenu = submenu?.querySelector(
      ".dropdown-menu"
    ) as HTMLElement;

    if (dropdownMenu) {
      // Toggle the show class
      if (dropdownMenu.classList.contains("show")) {
        dropdownMenu.classList.remove("show");
      } else {
        // Close other open submenus first
        const allSubmenus = document.querySelectorAll(
          ".dropdown-submenu .dropdown-menu.show"
        );
        allSubmenus.forEach((menu) => menu.classList.remove("show"));

        // Open this submenu
        dropdownMenu.classList.add("show");
      }
    }
  }
}
